if (!customElements.get('quick-add-sizes')) {
  /* ---------- shared measuring (this is what avoids the forced reflow) ----------
   *
   * Reading scrollWidth / clientWidth / scrollLeft makes the browser finish any pending layout
   * on the spot. With one <quick-add-sizes> per product card, doing that from connectedCallback
   * (and again from requestAnimationFrame / fonts.ready) meant: read -> write attribute ->
   * read -> write attribute ... across every card, each read re-flushing layout.
   *
   * Instead, one ResizeObserver is shared by all instances. Its callback runs *after* the
   * browser has laid the page out, so the reads below are free. Every instance is measured first
   * and only then written to, so the writes can't invalidate layout between two reads.
   */
  const refresh = (hosts) => {
    const measured = [];
    for (const host of hosts) measured.push([host, host.measure()]); // reads only
    for (const [host, state] of measured) host.render(state); // writes only
  };

  const sharedObserver = new ResizeObserver((entries) => {
    const hosts = new Set();
    for (const entry of entries) {
      const host = entry.target.closest('quick-add-sizes');
      if (host) hosts.add(host);
    }
    refresh(hosts);
  });

  customElements.define(
    'quick-add-sizes',
    class QuickAddSizes extends HTMLElement {
      constructor() {
        super();

        this.observed = [];
        this.onPageScroll = () => this.closeMenus();

        // Delegated listeners, attached on the host itself, so they work no matter when the
        // browser parsed the children. scroll / toggle don't bubble, hence the capture phase.
        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('scroll', (event) => this.onScroll(event), true);
        this.addEventListener('toggle', (event) => this.onToggle(event), true);
        this.addEventListener('submit', (event) => this.onSubmit(event));
      }

      connectedCallback() {
        // No measuring here: observe() makes the ResizeObserver deliver the first measurement
        // right after the browser's own layout pass, instead of forcing layout from script.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.observe(), { once: true });
        } else {
          this.observe();
        }
      }

      disconnectedCallback() {
        this.unobserve();
        window.removeEventListener('scroll', this.onPageScroll);
      }

      get track() {
        return this.querySelector('.quick-add-sizes__track');
      }

      observe() {
        const track = this.track;
        if (!this.isConnected || !track) return;

        this.unobserve();

        // The track catches width changes (window resize, mobile expand). The size buttons catch
        // content changes that don't resize the track, e.g. a web font swapping in.
        this.observed = [track, ...track.children];
        this.observed.forEach((el) => sharedObserver.observe(el));
      }

      unobserve() {
        this.observed.forEach((el) => sharedObserver.unobserve(el));
        this.observed = [];
      }

      /* ---------- carousel ---------- */

      // Kept as the public entry point. Prefer not to call it right after changing the DOM
      // yourself: that is a synchronous layout read.
      update() {
        refresh([this]);
      }

      // Reads only.
      measure() {
        const track = this.track;
        if (!track) return null;

        if (this.classList.contains('quick-add-sizes--two_rows')) {
          return { overflow: false, prevDisabled: true, nextDisabled: true };
        }

        const max = track.scrollWidth - track.clientWidth;

        return {
          overflow: max > 1,
          prevDisabled: track.scrollLeft <= 1,
          nextDisabled: track.scrollLeft >= max - 1,
        };
      }

      // Writes only, and only when something actually changes.
      render(state) {
        if (!state) return;

        if (this.hasAttribute('data-overflow') !== state.overflow) {
          this.toggleAttribute('data-overflow', state.overflow);
        }

        const prev = this.querySelector('[data-direction="prev"]');
        const next = this.querySelector('[data-direction="next"]');

        if (prev && prev.disabled !== state.prevDisabled) prev.disabled = state.prevDisabled;
        if (next && next.disabled !== state.nextDisabled) next.disabled = state.nextDisabled;
      }

      scrollPage(direction) {
        const track = this.track;
        if (!track) return;
        track.scrollBy({ left: direction * track.clientWidth * 0.75, behavior: 'smooth' });
      }

      onScroll(event) {
        if (event.target !== this.track) return;
        this.closeMenus();
        refresh([this]);
      }

      /* ---------- "M / M Tall" dropdown (native popover) ---------- */

      isOpen(menu) {
        try {
          return menu.matches(':popover-open');
        } catch (error) {
          return false;
        }
      }

      closeMenus() {
        this.querySelectorAll('.quick-add-sizes__menu').forEach((menu) => menu.hidePopover?.());
      }

      onClick(event) {
        // Expand mobile trigger
        const trigger = event.target.closest('.quick-add__mobile-trigger');
        if (trigger) {
          this.classList.add('is-expanded');
          // Runs in a later task, so layout is already clean by the time it reads
          setTimeout(() => refresh([this]), 300);
          return;
        }

        const arrow = event.target.closest('[data-direction]');
        if (arrow) {
          this.scrollPage(arrow.dataset.direction === 'next' ? 1 : -1);
          return;
        }

        const toggle = event.target.closest('.quick-add-sizes__button--toggle');
        if (!toggle) return;

        const menu = document.getElementById(toggle.getAttribute('popovertarget'));
        if (!menu || this.isOpen(menu)) return;

        // The popover opens right after this click handler finishes. Give it a position now so it
        // never shows up at the viewport's top-left corner, then fine-tune once it has a size.
        const rect = toggle.getBoundingClientRect();
        menu.style.top = `${rect.bottom + 4}px`;
        menu.style.left = `${rect.left}px`;
        requestAnimationFrame(() => this.placeMenu(menu, toggle));
      }

      placeMenu(menu, toggle) {
        if (!toggle || !this.isOpen(menu)) return;

        const gap = 4;
        const edge = 8;
        const toggleRect = toggle.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();

        // keep it inside the viewport horizontally
        const left = Math.max(edge, Math.min(toggleRect.left, window.innerWidth - menuRect.width - edge));

        // under the button, or above it when there is no room below
        let top = toggleRect.bottom + gap;
        const flippedTop = toggleRect.top - menuRect.height - gap;
        if (top + menuRect.height > window.innerHeight - edge && flippedTop > edge) top = flippedTop;

        menu.style.left = `${left}px`;
        menu.style.top = `${top}px`;
      }

      onToggle(event) {
        const menu = event.target;
        if (!menu.classList || !menu.classList.contains('quick-add-sizes__menu')) return;

        const toggle = menu.previousElementSibling;
        const isOpen = event.newState === 'open';
        toggle?.setAttribute('aria-expanded', String(isOpen));

        if (isOpen) {
          this.placeMenu(menu, toggle);
          // the dropdown is fixed to the viewport, so close it if the page scrolls away from the button
          window.addEventListener('scroll', this.onPageScroll, { passive: true });
        } else {
          window.removeEventListener('scroll', this.onPageScroll);
        }
      }

      onSubmit(event) {
        // close the dropdown as soon as a size is chosen
        const menu = event.target.closest('.quick-add-sizes__menu');
        menu?.hidePopover?.();
      }
    }
  );
}